import React, { useEffect, useState, useCallback } from "react";
import Form from "../HelperComponents/Form/Form";
import { Icon } from "@iconify-icon/react";
import LoadingSpinner from "../HelperComponents/LoadingSpinner";
import { handleUpdate } from "../HelperComponents/Form/FormHooks";
import MessageTile from "../HelperComponents/MessageTile";
import { getUserProfileForCurrentUser } from "../Service/Services";
import { useUser } from "../Context/UserContext";

type EditProfileProps = {
  staff?: any; // Made optional - TODO: fetch staff data within component if needed
};

export const getDemographicFields = (userProfile?: Record<string, any>) => [
  {
    id: "gender",
    title: "Gender",
    value: userProfile?.Gender,
    type: "List",
    options: ["Male", "Female", "Intersex", "Opt Out"],
    visible: true,
    required: true,
    destinations: [
      { destination: "userProfile", fieldName: "Gender" },
      { destination: "DFC", fieldName: "Gender" },
    ],
  },
  {
    id: "race",
    title: "Race",
    value: userProfile?.["Race-Ethnicity"],
    type: "List",
    options: [
      "American Indian or Alaska Native",
      "Asian",
      "Black or African American",
      "Native Hawaiian or Other Pacific Islander",
      "White",
      "Opt Out",
    ],
    visible: true,
    required: true,
    destinations: [{ destination: "userProfile", fieldName: "Race-Ethnicity" }],
  },
  {
    id: "ethnicity",
    title: "Ethnicity",
    value: userProfile?.Ethnicity,
    type: "List",
    options: ["Hispanic or Latino", "Not Hispanic or Latino", "Opt Out"],
    visible: true,
    required: true,
    destinations: [{ destination: "userProfile", fieldName: "Ethnicity" }],
  },
  {
    id: "veteranStatus",
    title: "Veteran Status",
    value: userProfile?.VeteranStatus,
    type: "List",
    options: [
      "Protected Veteran",
      "Veteran (Not Protected)",
      "Not a Veteran",
      "Opt Out",
    ],
    visible: true,
    required: true,
    destinations: [{ destination: "userProfile", fieldName: "VeteranStatus" }],
  },
];

const EditProfile = (props: EditProfileProps) => {
  const siteUrl = "https://sharepoint.teampbs.com";
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<
    "info" | "success" | "error" | "warning"
  >("info");
  const [userProfile, setUserProfile] = useState<any>();
  const { user } = useUser();

  // Button container styles
  const buttonContainerStyles = "editProfile-buttonContainer";
  // Modern styling variables
  const modernStyles = {
    borderRadius: "6px",
    fieldHeight: "35px",
    focusShadow: "0 0 0 2px rgba(0, 120, 212, 0.2)",
    hoverBorderColor: "hsl(var(--primary))",
    activeBorderColor: "hsl(var(--primary))",
    labelColor: "hsl(var(--foreground))",
    placeholderColor: "hsl(var(--foreground))",
    spacing: "16px",
    descriptionColor: "hsl(var(--foreground))",
    backgroundColor: "hsl(var(--primary))",
    hoveredBackgroundColor: "hsl(var(--primary))",
  };

  const fetchUserProfileProperties = useCallback(async () => {
    if (!user?.LoginName) return;
    try {
      setLoading(true);
      const response = await getUserProfileForCurrentUser(user.LoginName);
      setUserProfile(response.data.properties);
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [user?.LoginName]);

  useEffect(() => {
    if (user) {
      fetchUserProfileProperties();
    }
  }, [user, fetchUserProfileProperties]);

  const BasicInfoFields = [
    ...getDemographicFields(userProfile),
    {
      id: "languagesSpoken",
      title: "Languages Spoken",
      value: userProfile?.Languagesspoken,
      listName: "Languages",
      dropdownText: ["name"],
      type: "List",
      visible: true,
      multi: true,
      destinations: [
        { destination: "userProfile", fieldName: "Languagesspoken" },
      ],
    },
    {
      id: "dateOfBirth",
      title: "Date of Birth",
      value: userProfile?.BirthDate,
      type: "Date",
      visible: true,
      destinations: [{ destination: "userProfile", fieldName: "BirthDate" }],
    },
    {
      id: "Countryofbirth",
      title: "Country of Birth",
      value: userProfile?.Countryofbirth,
      type: "List",
      listName: "Countries",
      dropdownText: ["Title"],
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "Countryofbirth" },
      ],
    },
    {
      id: "stateOfBirth",
      title: "State of Birth",
      listName: "State",
      dropdownText: ["Code"],
      value: userProfile?.Stateofbirth,
      type: "List",
      visible: true,
      destinations: [{ destination: "userProfile", fieldName: "Stateofbirth" }],
    },
    {
      id: "cityOfBirth",
      title: "City of Birth",
      value: userProfile?.CityOfBirth,
      type: "Text",
      visible: true,
      destinations: [{ destination: "userProfile", fieldName: "CityOfBirth" }],
    },
    {
      id: "mobileNumber",
      title: "Mobile Number",
      value: userProfile?.CellPhone,
      type: "Phone",
      visible: true,
      description:
        "Enter your CELL PHONE number here without the country code. It will be shown on your profile. Also, it will be used for text message (SMS) alerts.",
      destinations: [
        { destination: "userProfile", fieldName: "CellPhone" },
        { destination: "DFC", fieldName: "i90b" },
      ],
    },
    {
      id: "homeNumber",
      title: "Home Phone",
      value: userProfile?.HomePhone,
      type: "Phone",
      visible: true,
      destinations: [{ destination: "userProfile", fieldName: "HomePhone" }],
    },
    {
      id: "personalEmail",
      title: "Personal Email",
      value: userProfile?.PersonalEmail,
      type: "Text",
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "PersonalEmail" },
        { destination: "DFC", fieldName: "Personal_x0020_Email" },
      ],
    },
  ];
  const AddressFields = [
    {
      id: "homeaddressLabel",
      title: "Home Address",
      type: "Label",
      visible: true,
    },
    {
      id: "streetAddress",
      title: "Street Address",
      value: userProfile?.Streetaddress,
      type: "Text",
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "Streetaddress" },
        { destination: "DFC", fieldName: "Address" },
      ],
    },
    {
      id: "streetAddressLine2",
      title: "Street Address Line 2",
      value: userProfile?.Streetaddressline2,
      type: "Text",
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "Streetaddressline2" },
      ],
    },
    {
      id: "city",
      title: "City",
      value: userProfile?.City,
      type: "Text",
      visible: true,
      destinations: [{ destination: "userProfile", fieldName: "City" }],
    },
    {
      id: "state",
      title: "State",
      value: userProfile?.State,
      type: "List",
      listName: "State",
      dropdownText: ["Code"],
      visible: true,
      destinations: [{ destination: "userProfile", fieldName: "State" }],
    },
    {
      id: "postCode",
      title: "Post Code",
      value: userProfile?.Zip,
      type: "Text",
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "Zip" },
        { destination: "DFC", fieldName: "ZIP" },
      ],
    },
    {
      id: "countyOfResidence",
      title: "County of Residence",
      value: userProfile?.CountyofResidence,
      type: "List",
      listName: "County",
      dropdownText: ["County"],
      visible: true,
      description: "Please provide a valid County Of Residence.",
      destinations: [
        { destination: "userProfile", fieldName: "CountyofResidence" },
      ],
    },
    {
      id: "sameMailingAndHomeAddress",
      title: "Mailing Address and Home Address are the same",
      value: userProfile?.SameMailingAndHomeAddress,
      type: "Checkbox",
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "SameMailingAndHomeAddress" },
      ],
    },
    {
      id: "homeaddressLabel",
      title: "Mailing Address",
      type: "Label",
      visible: true,
      visibilityConditions: [
        {
          fieldId: "sameMailingAndHomeAddress",
          operator: "notChecked",
        },
      ],
    },
    {
      id: "mailingStreetAddress",
      title: "Mailing Street Address",
      value: userProfile?.MailingStreetAddress,
      type: "Text",
      visible: true,
      visibilityConditions: [
        {
          fieldId: "sameMailingAndHomeAddress",
          operator: "notChecked",
        },
      ],
      destinations: [
        { destination: "userProfile", fieldName: "MailingStreetAddress" },
      ],
    },
    {
      id: "mailingStreetAddressLine2",
      title: "Mailing Street Address Line 2",
      value: userProfile?.Mailingstreetaddressline2,
      type: "Text",
      visible: true,
      visibilityConditions: [
        {
          fieldId: "sameMailingAndHomeAddress",
          operator: "notChecked",
        },
      ],
      destinations: [
        { destination: "userProfile", fieldName: "Mailingstreetaddressline2 " },
      ],
    },
    {
      id: "mailingCity",
      title: "Mailing City",
      value: userProfile?.MailingCity,
      type: "Text",
      visible: true,
      visibilityConditions: [
        {
          fieldId: "sameMailingAndHomeAddress",
          operator: "notChecked",
        },
      ],

      destinations: [{ destination: "userProfile", fieldName: "MailingCity" }],
    },
    {
      id: "mailingState",
      title: "Mailing State",
      listName: "State",
      dropdownText: ["Code"],
      value: userProfile?.MailingState,
      type: "List",
      visible: true,
      visibilityConditions: [
        {
          fieldId: "sameMailingAndHomeAddress",
          operator: "notChecked",
        },
      ],
      destinations: [{ destination: "userProfile", fieldName: "MailingState" }],
    },
    {
      id: "mailingPostCode",
      title: "Mailing Post Code",
      value: userProfile?.MailingZip,
      type: "Text",
      visible: true,
      visibilityConditions: [
        {
          fieldId: "sameMailingAndHomeAddress",
          operator: "notChecked",
        },
      ],
      destinations: [{ destination: "userProfile", fieldName: "MailingZip" }],
    },
  ];

  const AdditionalInfoFields = [
    {
      id: "emergencyContactName",
      title: "Emergency Contact Name",
      value: userProfile?.EmergencyContactName,
      type: "Text",
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "EmergencyContactName" },
        { destination: "DFC", fieldName: "Emergency_x0020_Contact_x0020_Na" },
      ],
    },
    {
      id: "emergencyContactNumber",
      title: "Emergency Contact Number",
      value: userProfile?.EmergencyContactNumber,
      type: "Phone",
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "EmergencyContactNumber" },
        { destination: "DFC", fieldName: "Emergency_x0020_Contact_x0020_Nu" },
      ],
    },
  ];

  const onUpdateClick = async () => {
    setMessage("");
    setLoading(true);
    // Yield to the browser so the spinner paints before the work starts
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      const result = await handleUpdate(
        [...BasicInfoFields, ...AddressFields, ...AdditionalInfoFields],
        userProfile.UserName,
        props.staff?.Id,
        siteUrl,
      );
      setMessageType(result.type);
      setMessage(result.message);
      setTimeout(() => {
        setMessage("");
      }, 3000);
      if (result.type === "success") {
        setTimeout(() => {
          window.location.reload();
        }, 1200);
      }
    } catch (err: any) {
      setMessageType((err && err.type) || "error");
      setMessage(err?.message || "Error updating profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="contentContainer">
        {message && (
          <MessageTile
            isVisible={!!message}
            message={message}
            type={messageType}
          />
        )}

        <div key={1} className="content">
          <h2 className="title">
            <Icon className="cardIcon" icon="lucide:edit" />
            Edit Profile
          </h2>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className="editProfile-container">
                <div className="editProfile-grid">
                  <div className="card">
                    <h3 className="sectionTitle">Basic Information</h3>
                    <Form fields={BasicInfoFields} columns={3} />
                  </div>
                  <div className="card">
                    <h3 className="sectionTitle">Address</h3>
                    <Form fields={AddressFields} columns={3} />
                  </div>
                  <div className="card">
                    <h3 className="sectionTitle">Additional Information</h3>
                    <Form fields={AdditionalInfoFields} columns={3} />
                  </div>
                </div>
              </div>
              <div className={buttonContainerStyles}>
                <button
                  type="button"
                  className="btn-primary"
                  style={{
                    minWidth: "10vw",
                    borderRadius: modernStyles.borderRadius,
                    background: modernStyles.backgroundColor,
                  }}
                  onClick={onUpdateClick}
                >
                  Update
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default EditProfile;
